const Pagy = (() => {
  const storageSupport = "sessionStorage" in window && "BroadcastChannel" in window
  let pagy = "pagy",
    storage: Storage,
    sync: BroadcastChannel,
    tabId: number
  if (storageSupport) {
    storage = sessionStorage
    sync = new BroadcastChannel(pagy)
    tabId = Date.now()
    sync.addEventListener("message", (e) => {
      if (e.data.from) {
        const cutoffs = storage.getItem(e.data.key)
        if (cutoffs) {
          sync.postMessage({ to: e.data.from, key: e.data.key, str: cutoffs })
        }
      } else if (e.data.to) {
        if (e.data.to == tabId) {
          storage.setItem(e.data.key, e.data.str)
        }
      }
    })
  }
  const rjsObserver = new ResizeObserver((entries) =>
    entries.forEach((e) => {
      e.target.querySelectorAll<HTMLElement & { render?: () => void }>(".pagy-rjs").forEach((el) => el.render?.())
    })
  )
  const B64SafeEncode = (unicode: string) =>
      btoa(String.fromCharCode(...new TextEncoder().encode(unicode))).replace(/[+/=]/g, (m) =>
        m == "+" ? "-" : m == "/" ? "_" : ""
      ),
    B64Decode = (base64: string) => new TextDecoder().decode(Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)))
  const randKey = () => Math.floor(Math.random() * 36 ** 3).toString(36)
  const augmentKeynav = async (
    nav: HTMLElement,
    [storageKey, rootKey, pageKey, last, spliceArgs]: [
      string | undefined,
      string | undefined,
      string,
      number,
      [number, number, ...unknown[]] | undefined
    ]
  ) => {
    let augmentPage: ((page: string) => string) | undefined
    const browserKey =
      document.cookie
        .split(/;\s+/)
        .find((row) => row.startsWith(pagy + "="))
        ?.split("=")[1] ?? randKey()
    document.cookie = pagy + "=" + browserKey
    if (storageKey && !(storageKey in storage)) {
      sync.postMessage({ from: tabId, key: storageKey })
      await new Promise((resolve) => setTimeout(() => resolve(""), 100))
      if (!(storageKey in storage)) {
        augmentPage = (page: string) => page + "+" + last
      }
    }
    if (!augmentPage) {
      if (!storageKey) {
        do {
          storageKey = randKey()
        } while (storageKey in storage)
      }
      const data = storage.getItem(storageKey),
        cutoffs = data ? JSON.parse(data) : [undefined]
      if (spliceArgs) {
        cutoffs.splice(...spliceArgs)
        storage.setItem(storageKey, JSON.stringify(cutoffs))
      }
      augmentPage = (page: string) => {
        const pageNum = parseInt(page)
        return B64SafeEncode(
          JSON.stringify([browserKey, storageKey, pageNum, cutoffs.length, cutoffs[pageNum - 1], cutoffs[pageNum]])
        )
      }
    }
    const search = rootKey ? `${rootKey}%5B${pageKey}%5D` : pageKey
    const re = new RegExp(`(?<=\\?.*)(\\b${search}=)(\\d+)`)
    for (const a of nav.querySelectorAll<HTMLAnchorElement>("a[href]")) {
      a.href = a.href.replace(re, (_match, prefix, digit) => `${prefix}${augmentPage!(digit)}`)
    }
    return augmentPage
  }
  const buildNavJs = (
    nav: HTMLElement & { render?: () => void },
    [
      [before, anchor, current, gap, after],
      pageToken,
      [widths, series, labels],
      keynavArgs,
    ]: [
      [string, string, string, string, string],
      string,
      [number[], (string | number)[][], (string | number)[][] | undefined],
      Parameters<typeof augmentKeynav>[1] | undefined
    ]
  ) => {
    const parent = nav.parentElement!
    let lastWidth = -1
    ;(nav.render = () => {
      const index = widths.findIndex((w) => w < parent.clientWidth)
      if (widths[index] === lastWidth) {
        return
      }
      let html = before
      series[index].forEach((item, i) => {
        html +=
          item == "gap"
            ? gap
            : (typeof item == "number" ? anchor.replace(pageToken, String(item)) : current).replace(
                "L<",
                (labels?.[index][i] ?? item) + "<"
              )
      })
      html += after
      nav.innerHTML = ""
      nav.insertAdjacentHTML("afterbegin", html)
      lastWidth = widths[index]
      if (keynavArgs && storageSupport) {
        augmentKeynav(nav, keynavArgs)
      }
    })()
    if (nav.classList.contains(pagy + "-rjs")) {
      rjsObserver.observe(parent)
    }
  }
  const initInputNavJs = async (
    nav: HTMLElement,
    [url_token, pageToken, keynavArgs]: [string, string, Parameters<typeof augmentKeynav>[1] | undefined]
  ) => {
    const augment =
      keynavArgs && storageSupport ? await augmentKeynav(nav, keynavArgs) : (page: string) => page
    initInput(nav, (inputValue: string) => url_token.replace(pageToken, augment(inputValue)))
  }
  const initLimitTagJs = (
    span: HTMLElement,
    [from, url_token, page_token, limitToken]: [number, string, string, string]
  ) => {
    initInput(span, (inputValue: string) => {
      return url_token
        .replace(page_token, String(Math.max(Math.ceil(from / parseInt(inputValue)), 1)))
        .replace(limitToken, inputValue)
    })
  }
  const initInput = (element: HTMLElement, getUrl: (inputValue: string) => string) => {
    const input = element.querySelector<HTMLInputElement>("input")!,
      link = element.querySelector<HTMLAnchorElement>("a")!,
      initial = input.value,
      action = () => {
        if (input.value === initial) {
          return
        }
        const [min, val, max] = [input.min, input.value, input.max].map((n) => parseInt(n) || 0)
        if (val < min || val > max) {
          input.value = initial
          input.select()
          return
        }
        link.href = getUrl(input.value)
        link.click()
      }
    input.addEventListener("focus", () => input.select())
    input.addEventListener("focusout", action)
    input.addEventListener("keypress", (e) => {
      if (e.key == "Enter") {
        action()
      }
    })
  }
  return {
    version: "43.2.7",
    init(arg?: HTMLElement | Document) {
      const target = arg instanceof HTMLElement ? arg : document,
        elements = target.querySelectorAll<HTMLElement>("[data-pagy]")
      for (const element of elements) {
        try {
          const [helperId, ...args] = JSON.parse(B64Decode(element.getAttribute("data-pagy")!)) as [
            string,
            ...unknown[]
          ]
          if (helperId == "k") {
            augmentKeynav(element, ...(args as [Parameters<typeof augmentKeynav>[1]]))
          } else if (helperId == "snj") {
            buildNavJs(element, args as Parameters<typeof buildNavJs>[1])
          } else if (helperId == "inj") {
            initInputNavJs(element, args as Parameters<typeof initInputNavJs>[1])
          } else if (helperId == "ltj") {
            initLimitTagJs(element, args as Parameters<typeof initLimitTagJs>[1])
          }
        } catch (err) {
          console.warn(`Pagy.init: %o\n%s`, element, err)
        }
      }
    },
  }
})()
export default Pagy
